import { dateUtils } from '@festina-life/core'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'

export type SortFn<T> = (left: T, right: T) => number

export const comparePrimitiveValues = <T extends unknown>({
  left,
  right,
  locale,
}: {
  left: T
  right: T
  locale: string
}): number => {
  if (left === right) {
    return 0
  }

  if (left === undefined || left === null) {
    return 1
  }

  if (right === undefined || right === null) {
    return -1
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(right) - Number(left)
  }

  if (
    typeof left === 'string' &&
    typeof right === 'string' &&
    isStrictServerDateString(left) &&
    isStrictServerDateString(right)
  ) {
    return right.localeCompare(left, locale, {
      numeric: true,
      sensitivity: 'base',
    })
  }

  return `${left}`.localeCompare(`${right}`, locale, {
    numeric: true,
    sensitivity: 'base',
  })
}

export const isStrictServerDateString = (value: string): boolean =>
  dayjs(value, dateUtils.SERVER_DATE_FORMAT, true).isValid()

export const shouldSortByRenderedValue = <T extends unknown>({
  leftValue,
  rightValue,
  leftRenderedValue,
  rightRenderedValue,
}: {
  leftValue: T
  rightValue: T
  leftRenderedValue: ReactNode
  rightRenderedValue: ReactNode
}): boolean => {
  if (
    typeof leftRenderedValue !== 'string' ||
    typeof rightRenderedValue !== 'string'
  ) {
    return false
  }

  if (
    isStrictServerDateString(leftRenderedValue) ||
    isStrictServerDateString(rightRenderedValue)
  ) {
    return false
  }

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return false
  }

  if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
    return false
  }

  if (
    typeof leftValue === 'string' &&
    typeof rightValue === 'string' &&
    (isStrictServerDateString(leftValue) ||
      isStrictServerDateString(rightValue))
  ) {
    return false
  }

  return true
}

export function getSortFn<T>({
  sort,
  locale,
}: {
  sort: 'automatic' | false | SortFn<T>
  locale: string
}):
  | ((
      left: { value: T; textValue: string },
      right: { value: T; textValue: string },
    ) => number)
  | undefined {
  if (sort === false) {
    return undefined
  }

  if (typeof sort === 'function') {
    return (left, right) => sort(left.value, right.value)
  }

  return (left, right) =>
    left.textValue !== '' &&
    right.textValue !== '' &&
    shouldSortByRenderedValue({
      leftValue: left.value,
      rightValue: right.value,
      leftRenderedValue: left.textValue,
      rightRenderedValue: right.textValue,
    })
      ? comparePrimitiveValues({
          left: left.textValue,
          right: right.textValue,
          locale,
        })
      : comparePrimitiveValues({
          left: left.value,
          right: right.value,
          locale,
        })
}
